import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import DropdownField from './DropdownField';
import { Button } from 'reactstrap';
import { connect } from 'react-redux';
import { set } from 'date-fns';
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
} from 'react-sortable-hoc';
import arrayMove from 'array-move';
import { Menu } from 'react-feather';
import * as Icon from 'react-feather';
import { debounce } from 'debounce';

const LayoutTagsField = ({ layoutTags, setTargetPrinterConfig }) => {
  const [tagToSelect, setTagToSelect] = useState(null);
  const [selectedTags, setSelectedTags] = useState(null);

  // Copy incoming layoutTags to selectedTags initially
  useEffect(() => {
    if (Array.isArray(selectedTags)) return; // only allow setting selectedTags initially (while null)

    // once layoutTags is initialized, copy its value to selectedTags
    if (Array.isArray(layoutTags)) {
      setSelectedTags(layoutTags);
    }
  }, [layoutTags]);

  // Listen to selectedTags changes and copy them to the targetPrinterConfig
  useEffect(() => {
    if (selectedTags && Array.isArray(selectedTags)) {
      setTargetPrinterConfig((prev) => ({
        ...prev,
        layoutTags: selectedTags,
      }));
    }
  }, [selectedTags]);

  const handleSelectTag = () => {
    if (!tagToSelect) return;
    setSelectedTags((prev) => [...prev, tagToSelect]);
    setTagToSelect(null);
  };

  const handleBulkSelectTags = (tags) => {
    setSelectedTags(tags);
  };

  const handleRemoveTag = (indexToRemove) => {
    setSelectedTags((prev) => prev.filter((_, i) => i !== indexToRemove));
  };

  const setTagParams = (index, key, value) => {
    setSelectedTags((prev) =>
      prev.map((tag, i) => {
        if (i !== index) return tag;
        return { ...tag, params: { ...(tag.params || {}), [key]: value } };
      }),
    );
  };

  const onSortEnd = ({ oldIndex, newIndex }) => {
    setSelectedTags((prev) => arrayMove(prev, oldIndex, newIndex));
  };

  // Sortable Components
  const SortableTagContainer = SortableContainer(({ children }) => (
    <div className="mb-2">{children}</div>
  ));

  const SortableTag = SortableElement(({ id, tag }) => {
    const isInstructionTag = tag.id.endsWith('Instruction');
    const includesTextParam = ['TextValue'].includes(tag.id);
    const includesMultiLineParam = [
      'OrderStoreAddressValue',
      'OrderStoreBillingAddressValue',
      'OrderDeliveryAddressValue',
      'OrderDeliveryPickupRequestedAtValue',
    ].includes(tag.id);

    const onToggle = (e, key) => {
      e.preventDefault();
      e.stopPropagation();
      setTagParams(id, key, !tag.params?.[key]);
    };

    return (
      <div className="d-flex align-items-center justify-content-between py-50 px-50">
        <span className="d-flex align-items-center">
          <SortableTagHandle />
          <span className="ml-2">{tag.id}</span>
        </span>

        <div className="d-flex align-items-center">
          {includesMultiLineParam && (
            <>
              <span
                className={`ml-75 cursor-pointer ${
                  tag.params?.multiLine ? 'text-primary' : 'text-muted'
                }`}
                title="Use multi line"
                onClick={(e) => onToggle(e, 'multiLine')}
              >
                <Icon.AlignJustify size={18} />
              </span>
            </>
          )}

          {includesTextParam && (
            <input
              type="text"
              className="form-control form-control-sm mr-50"
              style={{ width: 200 }}
              placeholder="Enter text"
              value={tag.params.text || ''}
              onBlur={(e) => setTagParams(id, 'text', e.target.value)}
            />
          )}

          {!isInstructionTag && (
            <>
              <span
                className={`ml-75 cursor-pointer ${
                  tag.params?.prependNewLine ? 'text-primary' : 'text-muted'
                }`}
                title="Prepend new line"
                onClick={(e) => onToggle(e, 'prependNewLine')}
              >
                <Icon.CornerRightUp size={18} />
              </span>

              <span
                className={`ml-75 cursor-pointer ${
                  tag.params?.withLineBreak ? 'text-primary' : 'text-muted'
                }`}
                title="With line break"
                onClick={(e) => onToggle(e, 'withLineBreak')}
              >
                <Icon.ChevronDown size={18} />
              </span>

              <span
                className={`ml-75 cursor-pointer ${
                  tag.params?.appendNewLine ? 'text-primary' : 'text-muted'
                }`}
                title="Append new line"
                onClick={(e) => onToggle(e, 'appendNewLine')}
              >
                <Icon.CornerLeftDown size={18} />
              </span>

              {/*<span className="ml-75 text-dark">|</span>*/}

              <span
                className={`ml-75 cursor-pointer ${
                  tag.params?.withLabel ? 'text-primary' : 'text-muted'
                }`}
                title="With label"
                onClick={(e) => onToggle(e, 'withLabel')}
              >
                <Icon.Tag size={18} />
              </span>

              {/*<span className="ml-75 text-dark">|</span>*/}
            </>
          )}

          <span
            className="ml-75 cursor-pointer text-danger"
            title="Delete"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleRemoveTag(id);
            }}
          >
            <Icon.Trash2 size={18} />
          </span>
        </div>
      </div>
    );
  });

  const SortableTagHandle = SortableHandle(() => (
    <span className="text-muted" style={{ cursor: 'grab' }}>
      <Menu size={16} />
    </span>
  ));

  return (
    <>
      <div className={'d-flex align-content-center justify-content-between '}>
        <div style={{ width: '60%' }} className="mr-2">
          <div className="mb-50 d-flex">
            <Button
              className="p-50 mr-50"
              size="sm"
              color="primary"
              onClick={() =>
                handleBulkSelectTags([
                  { id: 'TextAlignCenterInstruction' },
                  { id: 'FontSizeDoubleHeightAndWidthInstruction' },
                  { id: 'FontStyleSetBoldInstruction' },
                  {
                    id: 'OrderTypeValue',
                    params: { appendNewLine: true, withLineBreak: true },
                  },
                  { id: 'FontStyleUnsetBoldInstruction' },
                  { id: 'FontSizeNormalInstruction' },
                  { id: 'FontStyleSetBoldInstruction' },
                  {
                    id: 'OrderTableNameValue',
                    params: { withLabel: true, withLineBreak: true },
                  },
                  { id: 'FontStyleUnsetBoldInstruction' },
                  {
                    id: 'OrderNumberValue',
                    params: { withLabel: true, withLineBreak: true },
                  },
                  {
                    id: 'OrderInvoiceNumberValue',
                    params: { withLabel: true, withLineBreak: true },
                  },
                  {
                    id: 'OrderCreatedAtValue',
                    params: {
                      withLabel: true,
                      appendNewLine: false,
                      withLineBreak: true,
                    },
                  },
                  { id: 'FontStyleSetBoldInstruction' },
                  {
                    id: 'OrderDeliveryAddressValue',
                    params: {
                      multiLine: true,
                      withLabel: true,
                      appendNewLine: false,
                      withLineBreak: true,
                      prependNewLine: true,
                    },
                  },
                  {
                    id: 'OrderCustomerNameValue',
                    params: {
                      withLabel: true,
                      withLineBreak: true,
                      prependNewLine: true,
                    },
                  },
                  {
                    id: 'OrderCustomerTelValue',
                    params: { withLabel: true, withLineBreak: true },
                  },
                  { id: 'NewLineInstruction' },
                  { id: 'FontStyleUnsetBoldInstruction' },
                  { id: 'FontSizeDoubleHeightAndWidthInstruction' },
                  { id: 'FontStyleSetInvertInstruction' },
                  {
                    id: 'OrderDeliveryPickupRequestedAtValue',
                    params: {
                      multiLine: true,
                      withLabel: true,
                      appendNewLine: true,
                      withLineBreak: true,
                    },
                  },
                  { id: 'FontStyleUnsetInvertInstruction' },
                  {
                    id: 'OrderCollectionCodeValue',
                    params: {
                      withLabel: true,
                      appendNewLine: true,
                      withLineBreak: true,
                    },
                  },
                  {
                    id: 'OrderPagerNumberValue',
                    params: {
                      withLabel: true,
                      appendNewLine: true,
                      withLineBreak: true,
                    },
                  },
                  {
                    id: 'OrderItemsSection',
                    params: {
                      appendNewLine: true,
                      withLineBreak: true,
                      prependNewLine: true,
                    },
                  },
                  { id: 'FontStyleSetBoldInstruction' },
                  { id: 'FontSizeDoubleWidthInstruction' },
                  {
                    id: 'OrderCartCommentValue',
                    params: {
                      withLabel: false,
                      appendNewLine: true,
                      withLineBreak: true,
                      prependNewLine: false,
                    },
                  },
                  { id: 'FontStyleUnsetBoldInstruction' },
                  {
                    id: 'FooterSection',
                    params: { withLabel: true, withLineBreak: true },
                  },
                ])
              }
            >
              <FormattedMessage id="productionReceiptTemplateLabel" />
            </Button>
            <Button
              className="p-50"
              size="sm"
              color="primary"
              onClick={() =>
                handleBulkSelectTags([
                  { id: 'TextAlignCenterInstruction' },
                  {
                    id: 'StoreLogoSection',
                    params: {
                      withLabel: false,
                      withLineBreak: true,
                      prependNewLine: false,
                    },
                  },
                  { id: 'FontSizeDoubleHeightAndWidthInstruction' },
                  { id: 'FontStyleSetBoldInstruction' },
                  {
                    id: 'OrderReceiptTitleValue',
                    params: {
                      withLabel: false,
                      appendNewLine: true,
                      withLineBreak: true,
                    },
                  },
                  { id: 'FontSizeNormalInstruction' },
                  {
                    id: 'OrderStoreNameValue',
                    params: { withLabel: false, withLineBreak: true },
                  },
                  { id: 'FontStyleUnsetBoldInstruction' },
                  {
                    id: 'OrderStoreBillingAddressValue',
                    params: {
                      multiLine: true,
                      withLabel: false,
                      withLineBreak: true,
                    },
                  },
                  {
                    id: 'OrderStorePhoneValue',
                    params: { withLabel: true, withLineBreak: true },
                  },
                  {
                    id: 'OrderStoreEmailValue',
                    params: {
                      withLabel: true,
                      appendNewLine: true,
                      withLineBreak: true,
                    },
                  },
                  {
                    id: 'OrderNumberValue',
                    params: { withLabel: true, withLineBreak: true },
                  },
                  {
                    id: 'OrderInvoiceNumberValue',
                    params: { withLabel: true, withLineBreak: true },
                  },
                  {
                    id: 'OrderCreatedAtValue',
                    params: {
                      withLabel: true,
                      appendNewLine: true,
                      withLineBreak: true,
                    },
                  },
                  { id: 'FontSizeDoubleHeightAndWidthInstruction' },
                  {
                    id: 'OrderCollectionCodeValue',
                    params: {
                      withLabel: true,
                      appendNewLine: true,
                      withLineBreak: true,
                    },
                  },
                  {
                    id: 'OrderPagerNumberValue',
                    params: {
                      withLabel: true,
                      appendNewLine: true,
                      withLineBreak: true,
                    },
                  },
                  { id: 'FontSizeNormalInstruction' },
                  {
                    id: 'OrderItemsWithPricesSection',
                    params: {
                      withLabel: true,
                      appendNewLine: true,
                      withLineBreak: true,
                    },
                  },
                  {
                    id: 'OrderTaxLinesSection',
                    params: {
                      withLabel: true,
                      appendNewLine: true,
                      withLineBreak: true,
                    },
                  },
                  {
                    id: 'ScannerActionQRCodeSection',
                    params: { withLabel: true, withLineBreak: true },
                  },
                  {
                    id: 'FooterSection',
                    params: { withLabel: true, withLineBreak: true },
                  },
                ])
              }
            >
              <FormattedMessage id="invoiceTemplateLabel" />
            </Button>
          </div>
          <DropdownField
            groupClassName="w-100 p-0 mb-0"
            isClearable={true}
            isMulti={false}
            value={tagToSelect}
            options={[
              { id: 'TextAlignCenterInstruction' },
              {
                id: 'StoreLogoSection',
                params: {
                  withLabel: false,
                  withLineBreak: true,
                  prependNewLine: false,
                },
              },
              { id: 'FontSizeDoubleHeightAndWidthInstruction' },
              { id: 'FontStyleSetBoldInstruction' },
              {
                id: 'OrderInvoiceTitleValue',
                params: { appendNewLine: true, withLineBreak: true },
              },
              { id: 'FontSizeNormalInstruction' },
              { id: 'FontStyleSetBoldInstruction' },
              {
                id: 'OrderStoreNameValue',
                params: { withLabel: false, withLineBreak: true },
              },
              { id: 'FontStyleUnsetBoldInstruction' },
              {
                id: 'OrderStoreBillingAddressValue',
                params: {
                  multiLine: true,
                  withLabel: false,
                  withLineBreak: true,
                },
              },
              {
                id: 'OrderStorePhoneValue',
                params: { withLabel: true, withLineBreak: true },
              },
              {
                id: 'OrderStoreEmailValue',
                params: {
                  withLabel: true,
                  appendNewLine: true,
                  withLineBreak: true,
                },
              },
              {
                id: 'OrderTypeValue',
                params: { withLabel: true, withLineBreak: true },
              },
              {
                id: 'OrderNumberValue',
                params: { withLabel: true, withLineBreak: true },
              },
              {
                id: 'OrderInvoiceNumberValue',
                params: { withLabel: true, withLineBreak: true },
              },
              {
                id: 'OrderCreatedAtValue',
                params: {
                  withLabel: true,
                  appendNewLine: true,
                  withLineBreak: true,
                },
              },
              { id: 'FontSizeDoubleHeightAndWidthInstruction' },
              {
                id: 'OrderCollectionCodeValue',
                params: {
                  withLabel: true,
                  appendNewLine: true,
                  withLineBreak: true,
                },
              },
              {
                id: 'OrderPagerNumberValue',
                params: {
                  withLabel: true,
                  appendNewLine: true,
                  withLineBreak: true,
                },
              },
              {
                id: 'TextValue',
                params: {
                  text: '',
                },
              },
              {
                id: 'OrderTableNameValue',
                params: { withLabel: true, withLineBreak: true },
              },
              {
                id: 'OrderItemsWithPricesSection',
                params: {
                  withLabel: true,
                  appendNewLine: true,
                  withLineBreak: true,
                },
              },
              {
                id: 'OrderTaxLinesSection',
                params: {
                  withLabel: true,
                  appendNewLine: true,
                  withLineBreak: true,
                },
              },
              {
                id: 'ScannerActionQRCodeSection',
                params: { withLabel: true, withLineBreak: true },
              },
              {
                id: 'FooterSection',
                params: { withLabel: true, withLineBreak: true },
              },
            ]}
            getOptionLabel={(tag) => tag?.id}
            getOptionValue={(tag) => tag?.id}
            menuPlacement="top"
            propertyName="layoutTag"
            onChange={(tag) => {
              setTagToSelect(tag);
            }}
          />
        </div>

        <div
          style={{ width: '40%', minWidth: '115px' }}
          className="d-flex align-items-center justify-content-end setting-line"
        >
          <Button
            className="w-100"
            color="primary"
            onClick={handleSelectTag}
            disabled={!tagToSelect}
          >
            <FormattedMessage id="add" />
          </Button>
        </div>
      </div>

      {!!selectedTags?.length && (
        <div className="mt-1">
          <SortableTagContainer
            onSortEnd={onSortEnd}
            useDragHandle={true}
            helperClass="sortable-on-drag"
          >
            {selectedTags.map((tag, index) => (
              <SortableTag
                key={`tag-${index}`}
                index={index}
                id={index}
                tag={tag}
              />
            ))}
          </SortableTagContainer>
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  store: state.store.store,
});

export default connect(mapStateToProps)(LayoutTagsField);
